import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GraduationCap, ChevronDown, Flag, BookOpen, Target } from 'lucide-react';

const LEVELS = [
  {
    icon: '🟢', cefr: 'A1',
    title_fr: 'Niveau 1 — Débutant', title_en: 'Level 1 — Beginner',
    start_fr: 'Tu pars de zéro, sans aucune base en anglais.',
    start_en: 'You start from scratch, with no English at all.',
    learn_fr: ['Alphabet & prononciation', 'Salutations et politesse', 'Se présenter', 'Les nombres et l\'âge', 'Jours, mois et dates', 'Couleurs et objets du quotidien', 'Les verbes to be et to have (+ conjugaison)'],
    learn_en: ['Alphabet & pronunciation', 'Greetings and politeness', 'Introducing yourself', 'Numbers and age', 'Days, months and dates', 'Colours and everyday objects', 'The verbs to be and to have (+ conjugation)'],
    goals_fr: ['Te présenter et épeler ton nom', 'Saluer et être poli', 'Compter et dire ton âge', 'Former des phrases simples avec to be / to have'],
    goals_en: ['Introduce yourself and spell your name', 'Greet people politely', 'Count and give your age', 'Build simple sentences with to be / to have'],
  },
  {
    icon: '🟡', cefr: 'A2',
    title_fr: 'Niveau 2 — Élémentaire', title_en: 'Level 2 — Elementary',
    start_fr: 'Tu connais les bases (to be, to have, te présenter).',
    start_en: 'You know the basics (to be, to have, introducing yourself).',
    learn_fr: ['Verbes essentiels (+ conjugaison)', 'Présent simple vs présent continu', 'Ta routine et l\'heure', 'Nourriture et commander au restaurant', 'Shopping et argent', 'Demander son chemin', 'La météo et les saisons'],
    learn_en: ['Essential verbs (+ conjugation)', 'Present simple vs present continuous', 'Your routine and telling the time', 'Food and ordering at a restaurant', 'Shopping and money', 'Asking for directions', 'Weather and seasons'],
    goals_fr: ['Parler de ta vie quotidienne', 'Poser et répondre à des questions simples', 'Commander au restaurant et faire des achats', 'Demander et donner une direction'],
    goals_en: ['Talk about your daily life', 'Ask and answer simple questions', 'Order food and go shopping', 'Ask for and give directions'],
  },
  {
    icon: '🟠', cefr: 'B1',
    title_fr: 'Niveau 3 — Intermédiaire', title_en: 'Level 3 — Intermediate',
    start_fr: 'Tu maîtrises le présent et le vocabulaire du quotidien.',
    start_en: 'You are comfortable with the present and everyday vocabulary.',
    learn_fr: ['Passé simple (+ conjugaison)', 'Present perfect (+ conjugaison)', 'Le futur : will / going to (+ conjugaison)', 'Comparatifs et superlatifs', 'Voyager et l\'aéroport', 'La santé et chez le médecin', 'Prononciation améliorée'],
    learn_en: ['Past simple (+ conjugation)', 'Present perfect (+ conjugation)', 'The future: will / going to (+ conjugation)', 'Comparatives and superlatives', 'Travelling and the airport', 'Health and at the doctor', 'Improved pronunciation'],
    goals_fr: ['Raconter des événements au passé', 'Parler de tes projets futurs', 'Comparer des choses et des personnes', 'Voyager seul et gérer des situations réelles en anglais'],
    goals_en: ['Tell stories in the past', 'Talk about your future plans', 'Compare things and people', 'Travel alone and handle real-life situations in English'],
  },
  {
    icon: '🔵', cefr: 'B2',
    title_fr: 'Niveau 4 — Avancé', title_en: 'Level 4 — Advanced',
    start_fr: 'Tu sais parler au passé, présent et futur.',
    start_en: 'You can speak in the past, present and future.',
    learn_fr: ['Les conditionnels (les 4 types)', 'Le discours rapporté (+ concordance)', 'La voix passive (+ conjugaison)', 'Les propositions relatives', 'Idiomes et phrasal verbs', 'Exprimer et défendre une opinion', 'Écoute et expression fluides'],
    learn_en: ['Conditionals (all 4 types)', 'Reported speech (+ tense shift)', 'The passive voice (+ conjugation)', 'Relative clauses', 'Idioms and phrasal verbs', 'Expressing and defending opinions', 'Fluent listening and speaking'],
    goals_fr: ['Exprimer des idées complexes', 'Débattre et argumenter', 'Comprendre de longs textes et audios', 'Écrire des messages clairs et structurés'],
    goals_en: ['Express complex ideas', 'Debate and argue', 'Understand long texts and audio', 'Write clear, structured messages'],
  },
  {
    icon: '🟣', cefr: 'B2+',
    title_fr: 'Niveau 5 — Professionnel', title_en: 'Level 5 — Business',
    start_fr: 'Tu es à l\'aise à l\'oral et à l\'écrit.',
    start_en: 'You are comfortable speaking and writing.',
    learn_fr: ['Écrire des emails professionnels', 'Animer et participer à des réunions', 'Faire une présentation', 'Négocier des accords et des prix', 'Réussir un entretien d\'embauche', 'Téléphoner et développer son réseau'],
    learn_en: ['Writing professional emails', 'Leading and joining meetings', 'Giving a presentation', 'Negotiating deals and prices', 'Succeeding in job interviews', 'Telephoning and networking'],
    goals_fr: ['Travailler entièrement en anglais', 'Mener réunions, emails et présentations', 'Négocier avec assurance', 'Réussir un entretien d\'embauche en anglais'],
    goals_en: ['Work entirely in English', 'Handle meetings, emails and presentations', 'Negotiate with confidence', 'Pass a job interview in English'],
  },
  {
    icon: '🔴', cefr: 'C1',
    title_fr: 'Niveau 6 — Expert', title_en: 'Level 6 — Expert',
    start_fr: 'Tu communiques efficacement en contexte professionnel.',
    start_en: 'You communicate effectively in a professional context.',
    learn_fr: ['Idiomes avancés et slang', 'Registre, ton et formalité', 'Débattre de sujets abstraits', 'Parler sans préparation', 'Accent et intonation', 'Storytelling (temps du récit)', 'Prise de parole en public et persuasion'],
    learn_en: ['Advanced idioms and slang', 'Register, tone and formality', 'Debating abstract topics', 'Speaking without preparation', 'Accent and intonation', 'Storytelling (narrative tenses)', 'Public speaking and persuasion'],
    goals_fr: ['Parler avec naturel et précision', 'Convaincre et raconter avec aisance', 'Comprendre l\'anglais natif', 'T\'exprimer comme un locuteur quasi natif'],
    goals_en: ['Speak naturally and precisely', 'Persuade and tell stories with ease', 'Understand native English', 'Express yourself like a near-native speaker'],
  },
];

export default function CourseOverview({ lang = 'fr' }) {
  const [open, setOpen] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const fr = lang !== 'en';
  const visible = showAll ? LEVELS : LEVELS.slice(0, 3);
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-6" data-testid="course-overview">
      <div className="flex items-center gap-2 mb-1">
        <GraduationCap size={18} className="text-emerald-300" />
        <h3 className="font-bold text-lg">{fr ? 'Aperçu du cours' : 'Course overview'}</h3>
      </div>
      <p className="text-sm text-white/60 mb-4">
        {fr ? 'Ce que tu vas apprendre et savoir faire, niveau par niveau.' : 'What you will learn and be able to do, level by level.'}
      </p>

      <div className="space-y-2">
        {visible.map((lv, i) => {
          const isOpen = open === i;
          return (
            <div key={i} className="rounded-xl border border-white/10 bg-slate-900/40 overflow-hidden" data-testid={`overview-level-${i + 1}`}>
              <button
                onClick={() => setOpen(isOpen ? -1 : i)}
                data-testid={`overview-toggle-${i + 1}`}
                className="w-full flex items-center gap-3 p-3.5 text-left hover:bg-white/5 transition">
                <span className="text-2xl">{lv.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm">{fr ? lv.title_fr : lv.title_en}</div>
                  <div className="text-[11px] text-emerald-300/70 uppercase tracking-wider">CEFR {lv.cefr}</div>
                </div>
                <ChevronDown size={18} className={`text-white/40 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden">
                    <div className="px-4 pb-4 pt-1 space-y-4">
                      <div className="flex items-start gap-2.5">
                        <Flag size={15} className="text-white/40 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="text-[11px] uppercase tracking-wider text-white/40 mb-0.5">{fr ? 'Point de départ' : 'Starting point'}</div>
                          <div className="text-sm text-white/75">{fr ? lv.start_fr : lv.start_en}</div>
                        </div>
                      </div>

                      <div className="flex items-start gap-2.5">
                        <BookOpen size={15} className="text-emerald-400/70 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <div className="text-[11px] uppercase tracking-wider text-white/40 mb-1.5">{fr ? 'Ce que tu apprends' : 'What you learn'}</div>
                          <div className="flex flex-wrap gap-1.5">
                            {(fr ? lv.learn_fr : lv.learn_en).map((it, k) => (
                              <span key={k} className="text-[12px] bg-emerald-500/10 border border-emerald-500/25 text-emerald-100 rounded-full px-2.5 py-1">{it}</span>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-start gap-2.5">
                        <Target size={15} className="text-yellow-400/80 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <div className="text-[11px] uppercase tracking-wider text-white/40 mb-1.5">{fr ? 'À la fin de ce niveau, tu sais…' : 'By the end, you can…'}</div>
                          <ul className="space-y-1">
                            {(fr ? lv.goals_fr : lv.goals_en).map((g, k) => (
                              <li key={k} className="text-sm text-white/80 flex items-start gap-2">
                                <span className="text-emerald-400 mt-0.5">✓</span><span>{g}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      {LEVELS.length > 3 && (
        <button
          onClick={() => setShowAll(v => !v)}
          data-testid="course-overview-toggle"
          className="mt-3 w-full py-2.5 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/25 text-emerald-200 text-sm font-semibold transition">
          {showAll ? (fr ? 'Voir moins' : 'See less') : (fr ? `Voir plus (${LEVELS.length - 3})` : `See more (${LEVELS.length - 3})`)}
        </button>
      )}
    </div>
  );
}
